// Copyright (c) 2025, California Institute of Technology and contributors
//
// You should have received a copy of the licensing terms for this
// software included in the file "LICENSE" located in the top-level
// directory of this package. If you did not, you can view a copy at
// https://git.ligo.org/ngdd/arrakis-js/-/raw/main/LICENSE

import type * as Arrow from "apache-arrow";
import { gpsToUnix } from "./utils.js";

/**
 * Series block containing timeseries for channels for a given timestamp.
 */
export class SeriesBlock {
  /**
   * Creates a new SeriesBlock instance.
   *
   * @param time_ms - The GPS timestamp associated with this data, in miliseconds.
   * @param time_ns - The GPS timestamp associated with this data, in nanoseconds.
   * @param date - The corresponding UTC Date object
   * @param data - Mapping between channels and timeseries.
   * @param channels - Channel metadata associated with this data block.
   */
  constructor(
    public time_ms: number,
    public time_ns: number,
    public date: Date,
    public data: { [channel: string]: number[] },
    public channels: string[],
  ) {}

  /**
   * Creates a SeriesBlock from an Apache Arrow RecordBatch.
   *
   * This method extracts time-series data from an Arrow batch, converting it
   * into a SeriesBlock format. It expects the batch to have a "time" column
   * and additional columns for each data channel.
   *
   * @param batch - Apache Arrow RecordBatch containing the time-series data
   * @returns A new SeriesBlock instance created from the batch data
   *
   * @example
   * ```typescript
   * const batch = // ... Arrow RecordBatch
   * const block = SeriesBlock.fromBatch(batch);
   * console.log(block.time_ms); // GPS time in milliseconds
   * console.log(block.channels); // Array of channel names
   * console.log(block.data['channel1']); // Data array for 'channel1'
   * ```
   */
  static fromBatch(batch: Arrow.RecordBatch): SeriesBlock {
    const time_ns = Number(batch.getChild("time")?.get(0) ?? 0);
    const time_ms = Number((batch.getChild("time")?.get(0) ?? 0) / 1_000_000n);
    const channels = batch.schema.fields
      .map((field) => field.name)
      .filter((name) => name !== "time");
    const data = Object.fromEntries(
      channels.map((channel) => [
        channel,
        Array.from(batch.getChild(channel)?.get(0) ?? []) as number[],
      ]),
    ) as { [channel: string]: number[] };
    return new SeriesBlock(
      time_ms,
      time_ns,
      new Date(gpsToUnix(time_ms)),
      data,
      channels,
    );
  }
}

function setsAreEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const element of a) {
    if (!b.has(element)) return false;
  }
  return true;
}

/**
 * Join a sequence of SeriesBlock blocks into a single SeriesBlock.
 *
 * Each block after the first must start when the previous block ends
 * (matching timestamps as produced by sequential streaming). All blocks
 * must carry the same channel names in {@link SeriesBlock.data}.
 *
 * @param blocks - The timeseries blocks to concatenate
 * @returns The combined timeseries block starting at the first block's time
 */
export function concatenate(blocks: SeriesBlock[]): SeriesBlock {
  const first = blocks[0];
  if (blocks.length === 0 || first === undefined)
    throw new Error("concatenate requires at least one block");
  if (blocks.length === 1) return first;
  const channelSet = new Set(first.channels);
  const merged: { [channel: string]: number[] } = Object.fromEntries(
    Array.from(channelSet).map((channel: string) => [channel, []]),
  );
  let lastTimeMs = first.time_ms - 1;
  for (const block of blocks) {
    if (!setsAreEqual(new Set(block.channels), channelSet)) {
      throw new Error("all blocks must contain the same channel sets");
    }
    if (block.time_ms <= lastTimeMs) {
      throw new Error("blocks must be in time-ascending order");
    }
    for (const channel of block.channels) {
      merged[channel].push(...block.data[channel]);
    }
    lastTimeMs = block.time_ms;
  }
  return new SeriesBlock(
    first.time_ms,
    first.time_ns,
    new Date(gpsToUnix(first.time_ms)),
    merged,
    Array.from(channelSet),
  );
}
